import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Center, Code, Heading, Stack, Text } from '@chakra-ui/react';
import { RiftboundButton } from './ui/button';
import { isStaleDeployError, recoverFromStaleDeploy, reloadToFreshApp } from '../utils/pwaReload';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  recovering: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, recovering: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error', error, info.componentStack);
    if (!isStaleDeployError(error)) return;
    this.setState({ recovering: true });
    void recoverFromStaleDeploy().then((started) => {
      if (!started) this.setState({ recovering: false });
    });
  }

  handleReload = () => {
    this.setState({ recovering: true });
    void reloadToFreshApp('reset');
  };

  render() {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;

    const stale = isStaleDeployError(error);

    return (
      <Center minH="100dvh" px={6}>
        <Stack align="center" gap={5} maxW="480px" textAlign="center">
          <Heading size="2xl">
            {recovering || stale ? 'Updating the app' : 'Something went wrong'}
          </Heading>
          <Text color="textMuted">
            {recovering
              ? 'Loading the latest version…'
              : stale
                ? 'A new version was deployed and this tab still has cached files. Reload to pick it up — a normal refresh is not enough.'
                : 'An unexpected error occurred. Reload to get a clean copy of the app.'}
          </Text>
          {import.meta.env.DEV && (
            <Code
              display="block"
              whiteSpace="pre-wrap"
              fontSize="xs"
              p={3}
              borderRadius="md"
              textAlign="left"
              w="full"
            >
              {error.message}
            </Code>
          )}
          <Box>
            <RiftboundButton
              treatment="primary"
              onClick={this.handleReload}
              loading={recovering}
            >
              Reload
            </RiftboundButton>
          </Box>
        </Stack>
      </Center>
    );
  }
}
